import React, { forwardRef, useId } from 'react';
import styles from './TextField.module.css';

export type TextFieldSize = 'sm' | 'md' | 'lg';
export type TextFieldStatus = 'default' | 'error' | 'success';

export interface TextFieldProps extends Omit<React.ComponentPropsWithoutRef<'input'>, 'size'> {
  /** Floating label rendered above the input */
  label?: string;
  /** Helper or validation message rendered below the input */
  hint?: string;
  status?: TextFieldStatus;
  size?: TextFieldSize;
  /** Stretches the component to fill its container */
  fullWidth?: boolean;
  /** Icon or text placed inside the left edge */
  startAdornment?: React.ReactNode;
  /** Icon or text placed inside the right edge */
  endAdornment?: React.ReactNode;
}

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      hint,
      status = 'default',
      size = 'md',
      fullWidth = false,
      startAdornment,
      endAdornment,
      id: idProp,
      className,
      disabled,
      ...inputProps
    },
    ref,
  ) => {
    const generatedId = useId();
    const id = idProp ?? generatedId;

    const inputClasses = [
      styles.input,
      size !== 'md' && styles[size],
      status !== 'default' && styles[status],
      startAdornment && styles.withStartAdornment,
      endAdornment && styles.withEndAdornment,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={[styles.root, fullWidth && styles.fullWidth].filter(Boolean).join(' ')}>
        {label && (
          <label htmlFor={id} className={styles.label}>
            {label}
          </label>
        )}

        <div className={styles.inputWrapper}>
          {startAdornment && (
            <span className={`${styles.adornment} ${styles.start}`}>{startAdornment}</span>
          )}

          <input
            ref={ref}
            id={id}
            disabled={disabled}
            className={inputClasses}
            {...inputProps}
          />

          {endAdornment && (
            <span className={`${styles.adornment} ${styles.end}`}>{endAdornment}</span>
          )}
        </div>

        {hint && (
          <span className={[styles.hint, status !== 'default' && styles[status]].filter(Boolean).join(' ')}>
            {hint}
          </span>
        )}
      </div>
    );
  },
);

TextField.displayName = 'TextField';

export { TextField };
